import React, { useState, useEffect, useRef } from "react";
import { Text, Box } from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { getScheduledJobRunsByJobId } from "services/ScheduleServices";
import Table, { TableColumn, TablePagination } from "../common/Table";
import { useUser } from "context/UserContext";
import { epochConverterWithTimeZone } from "utils/dateUtils";

interface CustomAgentRunsSelectionProps {
  selectedItems?: Set<string>;
  onSelectionChange?: (selectedItems: Set<string>) => void;
  selectedCustomAgentId?: string[];
  hideActions?: boolean;
}

interface JobRunWithAgent extends Header {
  agentId: string;
  agentName: string;
}

const CustomAgentRunsSelection: React.FC<CustomAgentRunsSelectionProps> = ({
  selectedItems = new Set(),
  onSelectionChange,
  selectedCustomAgentId = [],
  hideActions = true,
}) => {
  const { defaultTimeZone } = useUser();
  const [jobRuns, setJobRuns] = useState<JobRunWithAgent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedData = useRef(false);

  // Fetch job runs for all selected custom agents only once
  useEffect(() => {
    const fetchJobRuns = async () => {
      if (selectedCustomAgentId.length === 0) {
        setJobRuns([]);
        hasFetchedData.current = true;
        return;
      }

      // Only fetch if we haven't fetched data yet
      if (hasFetchedData.current) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const allRuns: JobRunWithAgent[] = [];

        // Fetch runs for each selected agent
        for (const customAgentId of selectedCustomAgentId) {
          try {
            const runs = await getScheduledJobRunsByJobId(customAgentId);
            if (runs && runs.length > 0) {
              allRuns.push(...runs);
            }
          } catch (err) {
            console.error(`Error fetching runs for agent ${customAgentId}:`, err);
          }
        }

        setJobRuns(allRuns);
        hasFetchedData.current = true;
      } catch (err) {
        setError("Failed to fetch job runs");
        console.error("Error fetching job runs:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobRuns();
  }, [selectedCustomAgentId]);

  // Table columns
  const columns: TableColumn<JobRunWithAgent>[] = [
    {
      key: "name",
      label: "Run Name",
      sortable: true,
      render: (item) => (
        <Text fontSize="sm" fontWeight="medium" mb={0}>
          {item.name || `Run ${item.id?.slice(0, 8)}`}
        </Text>
      ),
    },
    {
      key: "created_date_ms",
      label: "Created Date",
      sortable: true,
      width: "180px",
      render: (item) => (
        <Text fontSize="sm" color="gray.600" mb={0}>
          {epochConverterWithTimeZone(item.created_date_ms || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    },
    {
      key: "last_modified_date_ms",
      label: "Last Modified",
      sortable: true,
      width: "180px",
      render: (item) => (
        <Text fontSize="sm" color="gray.600" mb={0}>
          {epochConverterWithTimeZone(item.last_modified_date_ms || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    },
  ];

  // Allow multiple runs selection
  const handleSelectionChange = (newSelectedItems: Set<string>) => {
    if (!onSelectionChange) return;
    onSelectionChange(newSelectedItems);
  };

  // Mock pagination (since we're not using real pagination)
  const pagination: TablePagination = {
    total: jobRuns.length,
    offset: 0,
    has_more: false,
  };

  return (
    <Box p={4}>
      <Table
        data={jobRuns}
        columns={columns}
        loading={isLoading}
        error={error}
        pagination={pagination}
        selectable={true}
        selectedItems={selectedItems}
        onSelectionChange={handleSelectionChange}
        getItemId={(item) => item.id || ""}
        emptyMessage="No job runs found"
        emptySubMessage="Try selecting different custom agents or check if they have any runs"
        hideSelectionCount={true}
      />
    </Box>
  );
};

export default CustomAgentRunsSelection;
